import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import User from '../models/user';
import { useQueryCurrentUser } from '@authentication/queries/userQueries';

import { LocalStorageKeys } from '@authentication/constants';
import { getItem } from '@/lib/storageUtil';

interface AuthContextData {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const AuthContext = createContext<AuthContextData | undefined>(
  undefined,
);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Set isLoading to true initially
  const storedUserId = getItem(LocalStorageKeys.ACCESS_TOKEN);
  const { data, isLoading: queryIsLoading } = useQueryCurrentUser(
    !!storedUserId,
  );

  useEffect(() => {
    if (user ?? queryIsLoading) return;
    if (data?.data) {
      setUser(data?.data.user);
    }
    setIsLoading(false);
  }, [queryIsLoading]);

  useEffect(() => {
    if (!storedUserId) setIsLoading(false);
  }, []);

  // Use useMemo to prevent the value object from changing on every render
  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      isLoading,
      setIsLoading,
    }),
    [user, setUser, isLoading, setIsLoading],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
